import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { type Moment } from 'moment';
import type React from 'react';
import { useEffect, useState } from 'react';
import { isRangeTooWide, MAX_RANGE_DAYS, maxRangeMessage } from 'utils/validateDateRange';

type DateRangeOption =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'last24Hours'
  | 'last7Days'
  | 'last30Days'
  | 'custom'
  | 'none';

interface DatePickerWithShortcutsProps {
  startDate: Moment | null;
  endDate: Moment | null;
  onChange: (startDate: Moment | null, endDate: Moment | null) => void;
  defaultOption?: DateRangeOption;
  /** Maximum number of days allowed between start and end (inclusive). Defaults to MAX_RANGE_DAYS (90). */
  maxRangeDays?: number;
}

/** Build the (start, end) tuple for each preset. */
const buildPresetRange = (option: DateRangeOption): [Moment, Moment] | null => {
  switch (option) {
    case 'last24Hours':
      return [moment().subtract(24, 'hours'), moment()];
    case 'today':
      return [moment().startOf('day'), moment().endOf('day')];
    case 'thisWeek':
      return [moment().startOf('week'), moment().endOf('week')];
    case 'thisMonth':
      return [moment().startOf('month'), moment().endOf('month')];
    case 'last7Days':
      return [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')];
    case 'last30Days':
      return [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')];
    default:
      return null;
  }
};

/**
 * A date picker component with predefined shortcuts for common date ranges.
 * Provides options for last 24 hours, last 7 days, last 30 days, and custom date range selection.
 * Enforces a hard maximum range (`maxRangeDays`) — wider shortcuts are disabled and custom ranges are rejected.
 *
 * @param {Moment | null} startDate - The current start date value
 * @param {Moment | null} endDate - The current end date value
 * @param {function} onChange - Callback function called when date range changes
 * @param {DateRangeOption} defaultOption - The default date range option (default: 'none')
 * @param {number} maxRangeDays - Maximum days allowed between start and end (default: MAX_RANGE_DAYS = 90)
 * @returns {JSX.Element} The DatePickerWithShortcuts component
 */
const DatePickerWithShortcuts: React.FC<DatePickerWithShortcutsProps> = ({
  startDate,
  endDate,
  onChange,
  defaultOption = 'none',
  maxRangeDays = MAX_RANGE_DAYS,
}) => {
  const [selectedOption, setSelectedOption] = useState<DateRangeOption>(defaultOption);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Moment | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Moment | null>(endDate);
  const [customError, setCustomError] = useState<string | null>(null);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  const fireBlockFeedback = (msg: string) => {
    setSnackbarMsg(msg);
  };

  /**
   * Handles the selection change from the dropdown
   *
   * @param {SelectChangeEvent} event - The select change event
   */
  const handleSelectionChange = (event: SelectChangeEvent) => {
    const value = event.target.value as DateRangeOption;

    if (value === 'none') {
      setSelectedOption(value);
      onChange(null, null);
      return;
    }

    if (value === 'custom') {
      setSelectedOption(value);
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setCustomError(null);
      setIsDialogOpen(true);
      return;
    }

    const range = buildPresetRange(value);
    if (!range) return;
    if (isRangeTooWide(range[0], range[1], maxRangeDays)) {
      // Hard block: surface snackbar feedback instead of silently ignoring the click.
      fireBlockFeedback(maxRangeMessage(maxRangeDays));
      return;
    }
    setSelectedOption(value);
    onChange(range[0], range[1]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only initialization preserved from previous behavior — must not re-fire on prop updates.
  useEffect(() => {
    if (defaultOption !== 'none' && !startDate && !endDate && selectedOption === defaultOption) {
      const range = buildPresetRange(defaultOption);
      if (range && !isRangeTooWide(range[0], range[1], maxRangeDays)) {
        onChange(range[0], range[1]);
      }
    }
  }, []);

  /**
   * Handles the confirmation of custom date range selection — hard-blocks when range exceeds maxRangeDays.
   */
  const handleCustomDateConfirm = () => {
    const normalizedStartDate = tempStartDate ? moment(tempStartDate).startOf('day') : tempStartDate;
    // End snaps to end-of-day (23:59:59.999) so the closing date is included in the query,
    // matching the preset shortcuts (which already use endOf('day')).
    const normalizedEndDate = tempEndDate ? moment(tempEndDate).endOf('day') : tempEndDate;
    if (isRangeTooWide(normalizedStartDate, normalizedEndDate, maxRangeDays)) {
      setCustomError(maxRangeMessage(maxRangeDays));
      fireBlockFeedback(maxRangeMessage(maxRangeDays));
      return;
    }
    setCustomError(null);
    onChange(normalizedStartDate, normalizedEndDate);
    setIsDialogOpen(false);
  };

  /**
   * Handles the cancellation of custom date range selection
   */
  const handleCustomDateCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsDialogOpen(false);
    setCustomError(null);
    if (startDate && endDate) {
      const daysDiff = endDate.diff(startDate, 'days');
      if (daysDiff === 0) {
        setSelectedOption('today');
      } else if (daysDiff === 7) {
        setSelectedOption('thisWeek');
      } else if (daysDiff === 30) {
        setSelectedOption('thisMonth');
      } else if (daysDiff <= 1 && endDate.diff(startDate, 'hours') <= 24) {
        setSelectedOption('last24Hours');
      } else if (daysDiff <= 6) {
        setSelectedOption('last7Days');
      } else if (daysDiff <= 29) {
        setSelectedOption('last30Days');
      } else {
        setSelectedOption('custom');
      }
    } else {
      setSelectedOption('none');
    }
  };

  const tempTooWide = isRangeTooWide(tempStartDate, tempEndDate, maxRangeDays);
  // Cap the "Hasta" picker to keep selection within the max window from the chosen start.
  const tempEndMax = tempStartDate ? tempStartDate.clone().add(maxRangeDays - 1, 'days') : undefined;
  const tempStartMin = tempEndDate ? tempEndDate.clone().subtract(maxRangeDays - 1, 'days') : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', flexDirection: 'column' }}>
        <FormControl size="small" sx={{ minWidth: 150, width: '100%', backgroundColor: 'background.default' }}>
          <InputLabel id="date-range-select-label">Rango de fechas</InputLabel>
          <Select
            labelId="date-range-select-label"
            value={selectedOption}
            label="Rango de fechas"
            onChange={handleSelectionChange}
            sx={{ height: '100%' }}
          >
            <MenuItem value="none">
              <em>Ninguno</em>
            </MenuItem>
            <MenuItem value="last24Hours">Últimas 24 horas</MenuItem>
            <MenuItem value="today">Hoy</MenuItem>
            <MenuItem value="thisWeek">Esta semana</MenuItem>
            <MenuItem value="thisMonth">Este mes</MenuItem>
            <MenuItem value="last7Days">Últimos 7 días</MenuItem>
            <MenuItem value="last30Days">Últimos 30 días</MenuItem>
            <MenuItem
              value="custom"
              onClick={() => {
                if (selectedOption === 'custom') {
                  setTempStartDate(startDate);
                  setTempEndDate(endDate);
                  setCustomError(null);
                  setIsDialogOpen(true);
                }
              }}
            >
              Personalizado
            </MenuItem>
          </Select>
          <FormHelperText>{`Máximo ${maxRangeDays} días por rango`}</FormHelperText>
        </FormControl>

        <Dialog open={isDialogOpen} onClose={handleCustomDateCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Seleccionar rango de fechas personalizado</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <DatePicker
                label="Fecha inicial"
                format="DD/MMM/YYYY"
                value={tempStartDate}
                onChange={(value) => {
                  setTempStartDate(value);
                  if (value && tempEndDate && isRangeTooWide(value, tempEndDate, maxRangeDays)) {
                    setTempEndDate(null);
                  }
                  setCustomError(null);
                }}
                maxDate={tempEndDate ?? undefined}
                minDate={tempStartMin ?? undefined}
                slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
              />
              <DatePicker
                label="Fecha final"
                format="DD/MMM/YYYY"
                value={tempEndDate}
                onChange={(value) => {
                  if (value && tempStartDate && isRangeTooWide(tempStartDate, value, maxRangeDays)) {
                    setCustomError(maxRangeMessage(maxRangeDays));
                    fireBlockFeedback(maxRangeMessage(maxRangeDays));
                    return;
                  }
                  setTempEndDate(value);
                  setCustomError(null);
                }}
                minDate={tempStartDate ?? undefined}
                maxDate={tempEndMax ?? undefined}
                slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
              />
            </Box>
            <Typography
              sx={{
                mt: 1.5,
                fontSize: '0.8rem',
                color: customError || tempTooWide ? '#c62828' : 'text.secondary',
              }}
            >
              {customError ?? (tempTooWide ? maxRangeMessage(maxRangeDays) : `Máximo ${maxRangeDays} días por rango.`)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCustomDateCancel}>Cancelar</Button>
            <Button onClick={handleCustomDateConfirm} variant="contained" disabled={tempTooWide}>
              Aplicar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarMsg !== null}
          autoHideDuration={4000}
          onClose={() => setSnackbarMsg(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="warning" variant="filled" onClose={() => setSnackbarMsg(null)}>
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default DatePickerWithShortcuts;
